import { InputField } from "@/components/common/input-field";
import { Button } from "@/components/ui/button";
import { contactSchema, type ContactValues } from "../schema/contact.schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth.store";
import { UpdateContact } from "@/services/bookinghistory.service";
import PhoneField from "./PhoneField";
import { useEffect, useState } from "react";
import type { DialCode } from "@/types/dialcode";
import { GetDialCode } from "@/services/dialcode.service";
import { cn } from "@/lib/utils";

const FIELD_CLASS =
  "h-14 rounded-2xl border-border bg-card px-4 pr-14 text-sm font-medium text-muted-foreground placeholder:text-border";

type EditContactDetailsFormProps = {
  contact: ContactValues;
};

export function EditContactDetailsForm({
  contact,
}: EditContactDetailsFormProps) {
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const [dialCodes, setDialCodes] = useState<DialCode[]>([]);
  const [loadingDialCodes, setLoadingDialCodes] = useState(true);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    mode: "onChange",
    defaultValues: {
      ...contact,
      countryCode: contact.countryCode || "+60",
      contactMobileNo: contact.contactMobileNo || "",
    },
  });

  useEffect(() => {
    const fetchDialCodes = async () => {
      try {
        setLoadingDialCodes(true);

        const response = await GetDialCode();

        setDialCodes(response.data?.dialCodeList ?? []);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to get dial codes.",
        );
      } finally {
        setLoadingDialCodes(false);
      }
    };

    fetchDialCodes();
  }, []);

  const onSubmit = async (data: ContactValues) => {
    try {
      await UpdateContact({
        agentCompanySellerId:
          useAuthStore.getState().user?.agentCompanySellerId || "",
        paymentId: paymentId || "",
        contactFirstName: data.firstName || "",
        contactLastName: data.lastName || "",
        contactEmail: data.email,
        countryCode: data.countryCode,
        contactMobileNo: data.contactMobileNo,
      });

      toast.success("Successfully updated contact details.");
      navigate(-1);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update contact details. Please try again later.",
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="rounded-2xl bg-card px-4 py-6 shadow-xl">
        <div className="flex flex-col gap-4 md:grid md:grid-cols-3">
          <InputField
            label="First Name"
            placeholder="Enter first name"
            required
            maxLength={50}
            className={cn(
              FIELD_CLASS,
              errors.firstName && "border-destructive",
            )}
            {...register("firstName")}
            error={errors.firstName?.message}
          />

          <InputField
            label="Last Name"
            placeholder="Enter last name"
            required
            maxLength={50}
            className={cn(
              FIELD_CLASS,
              errors.lastName && "border-destructive",
            )}
            {...register("lastName")}
            error={errors.lastName?.message}
          />

          <InputField
            label="Email Address"
            placeholder="Enter email address"
            required
            className={cn(FIELD_CLASS, errors.email && "border-destructive")}
            {...register("email")}
            error={errors.email?.message}
          />

          <PhoneField
            code={watch("countryCode")}
            phone={watch("contactMobileNo")}
            dialCodes={dialCodes}
            onCodeChange={(value) => {
              setValue("countryCode", value, {
                shouldValidate: true,
                shouldDirty: true,
              });

              trigger(["countryCode", "contactMobileNo"]);
            }}
            onPhoneChange={(value) => {
              const countryCode = watch("countryCode");

              // Keep maximum input at 15 digits
              const phone = value.replace(/\D/g, "").slice(0, 15);

              // Don't allow 0 as first digit for Malaysia
              if (countryCode === "+60" && phone.startsWith("0")) {
                return;
              }

              setValue("contactMobileNo", phone, {
                shouldValidate: true,
                shouldDirty: true,
              });

              // Validate immediately while typing
              trigger("contactMobileNo");
            }}
            error={
              errors.contactMobileNo?.message || errors.countryCode?.message
            }
          />
        </div>

        {/* Desktop */}
        <div className="hidden md:flex md:justify-end">
          <Button
            variant="default"
            className="mt-8 w-2xs cursor-pointer"
            type="submit"
            disabled={!isValid || isSubmitting || loadingDialCodes}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>

      {/* Mobile */}
      <Button
        variant="default"
        className="mt-8 w-full cursor-pointer md:hidden"
        type="submit"
        disabled={!isValid || isSubmitting || loadingDialCodes}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="animate-spin" />
            Saving...
          </>
        ) : (
          "Save"
        )}
      </Button>
    </form>
  );
}
